import { StepperContent } from "@app/components/ui/layout/stepperContent";
import React, { useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import SampleDrawKitAddAddress from "./sampleDrawKitAddAddress";
import {
  Address,
  KitContext,
  ProfileContext,
  TestDetailsContext,
} from "@app/provider";
import SampleDrawKitSelectAddress from "./sampleDrawKitSelectAddress";
import { getListOfAddresses } from "@app/utils";
import { useDialog } from "@natera/platform/lib/hooks";
import { SpinnerView } from "@app/components";
import KitSuccessDialog from "./kitSuccessDialog";
import KitErrorDialog from "./kitErrorDialog";
import SkipKitShippingDialog from "../../dialogs/skipKitShippingModal/skipKitShippingDialog";
import {
  AddressFormType,
  POBoxErrorNotification,
} from "@app/components/poBoxErrorNotification/poBoxErrorNotification";
import { isPOBoxOrPMB } from "@app/utils/isPOBoxOrPMB";

const messages = defineMessages({
  sampleDrawKitStepTitle: {
    id: "sampleDrawKitStepTitle",
    defaultMessage: "Kit Shipping Details",
  },
  sampleDrawKitStepDescriptionTitle: {
    id: "sampleDrawKitStepDescriptionTitle",
    defaultMessage: "Where should we send your kit?",
  },
  kitShippingAddressSelectDescriptionContent: {
    id: "kitShippingAddressSelectDescriptionContent",
    defaultMessage:
      "A test kit is needed to get your sample drawn. To have your kit shipped to a different address from the one shown, add a new address.",
  },
  kitShippingAddressEnterNewAddressDescriptionContent: {
    id: "kitShippingAddressEnterNewAddressDescriptionContent",
    defaultMessage:
      "A test kit is needed to get your sample drawn. To have your kit shipped, please enter a new address.",
  },
});

export enum ADDRESS_VIEW_TYPE {
  CURRENT = "CURRENT",
  NEW = "NEW",
}

export type SampleDrawKitProps = {
  onSubmit: () => Promise<void>;
  onBack?: () => Promise<void>;
  profileAddresses?: Address[];
  orderUidPublic?: string;
  testUid?: string;
  token?: string;
  isPublicMpFlow?: boolean;
};

const SampleDrawKitStep: React.FC<SampleDrawKitProps> = ({
  onSubmit,
  onBack,
  orderUidPublic: orderUidPublicProp,
  testUid: testUidProp,
  profileAddresses,
  token,
  isPublicMpFlow,
}) => {
  const intl = useIntl();
  const successDialog = useDialog(KitSuccessDialog);
  const errorDialog = useDialog(KitErrorDialog);
  const skipKitShippingDialog = useDialog(SkipKitShippingDialog);

  const { getProfile, getProfileData: profile } = useContext(ProfileContext);
  const { addKit, addKitIsLoading, setSkipKitShipping, setSkipLabStep } =
    useContext(KitContext);
  const { getOrderUidPublic, getTestUid } = useContext(TestDetailsContext);

  const orderUidPublic = orderUidPublicProp ?? getOrderUidPublic();
  const testUid = testUidProp ?? getTestUid();
  const addresses: Address[] = getListOfAddresses(
    profileAddresses ? profileAddresses : (profile?.addresses ?? [])
  );

  const hasAddresses = addresses.length > 0;

  const [selectedAddressView, setSelectedAddressView] =
    useState<ADDRESS_VIEW_TYPE>(
      hasAddresses ? ADDRESS_VIEW_TYPE.CURRENT : ADDRESS_VIEW_TYPE.NEW
    );
  const [showPOBoxError, setShowPOBoxError] = useState<boolean>(false);

  const closeStep = (closeDialog: () => void) => () => {
    closeDialog();
    onSubmit();
  };

  const handleSubmit = async (address: Partial<Address>) => {
    if (isPOBoxOrPMB(address.street1, address?.street2)) {
      setShowPOBoxError(true);
      return;
    }

    if (!address.countryCode) {
      address.countryCode = "US";
    }

    try {
      const { result, kitOrderSkippedForPR } = await addKit(
        orderUidPublic,
        testUid,
        address,
        token,
        isPublicMpFlow
      );
      await getProfile();

      if (result) {
        if (kitOrderSkippedForPR) {
          skipKitShippingDialog.open({
            onClick: () => {
              setSkipKitShipping(true);
              setSkipLabStep(true);
              closeStep(skipKitShippingDialog.close)();
            },
            onClose: skipKitShippingDialog.close,
          });
          return;
        }

        successDialog.open({ onClose: closeStep(successDialog.close) });
      } else {
        errorDialog.open({
          onClose: closeStep(errorDialog.close),
        });
      }
    } catch (error) {
      errorDialog.open({
        onClose: closeStep(errorDialog.close),
      });
    }
  };

  const onAddNewAddress = () => setSelectedAddressView(ADDRESS_VIEW_TYPE.NEW);
  const onCurrentAddress = () =>
    setSelectedAddressView(ADDRESS_VIEW_TYPE.CURRENT);

  const onBackForNewAddress = hasAddresses ? onCurrentAddress : onBack;

  useEffect(() => {
    setShowPOBoxError(false);
  }, [selectedAddressView]);

  return (
    <>
      {showPOBoxError && (
        <POBoxErrorNotification formType={AddressFormType.KIT} />
      )}
      <StepperContent
        title={intl.formatMessage(messages.sampleDrawKitStepTitle)}
        subTitle={intl.formatMessage(
          messages.sampleDrawKitStepDescriptionTitle
        )}
        description={
          selectedAddressView === ADDRESS_VIEW_TYPE.CURRENT
            ? intl.formatMessage(
                messages.kitShippingAddressSelectDescriptionContent
              )
            : intl.formatMessage(
                messages.kitShippingAddressEnterNewAddressDescriptionContent
              )
        }
      >
        <>
          {successDialog.getDialog()}
          {errorDialog.getDialog()}
          {skipKitShippingDialog.getDialog()}
          <SpinnerView isLoading={addKitIsLoading} />
          {selectedAddressView === ADDRESS_VIEW_TYPE.CURRENT ? (
            <SampleDrawKitSelectAddress
              addresses={addresses}
              onAddNewAddress={onAddNewAddress}
              onSubmit={handleSubmit}
              onBack={onBack}
            />
          ) : (
            <SampleDrawKitAddAddress
              onSubmit={handleSubmit}
              onBack={onBackForNewAddress}
            />
          )}
        </>
      </StepperContent>
    </>
  );
};

export default SampleDrawKitStep;
